import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["userId"];
import Platform from "../RECSender/Platform.js";
import cookies from '@deezer/cookies';
import { initSegmentSnippet } from "./segment.js";
// Partners for first party cookie
const partners = ['gclid', 'ext_publisher_id', 'rtid', 'ttclid', 'ScCid', 'irclickid'];
const partnersInCookies = [...partners, '_fbp', '_fbc', 'fb_login_id'];
export const getDefaultParams = () => {
  const params = {};
  partnersInCookies.map(cookieName => {
    /*
     * Handle the case where we need to transform the cookie name by removing the underscore
     * and lowercasing it
     */
    const paramName = cookieName.replace(/_/, '').toLowerCase();

    // Retrieve the cookie value
    const cookieValue = cookies.get(paramName);
    if (cookieValue) {
      params[paramName] = cookieValue;
    }
  });
  return Object.keys(params).length ? params : undefined;
};
const isPageViewEvent = e => {
  return e && e.eventName === 'pageView' || e && e.eventName === 'screen_view';
};
export class CDP {
  /**
   * @param apiKey Required to load appropriate snippet
   * @param options Contain default values,
   * you can define multiple things like the name of the app, dev env, minimum event logging level (useful for debug),
   * filter (used to restrict usage of cdp, ex: for country)
   */
  constructor(apiKey, options = {
    filter: true,
    minimumEventVersion: 1
  }) {
    this.apiKey = void 0;
    this.options = {
      filter: true,
      filterFunction: () => true,
      minimumEventVersion: 1,
      name: '',
      stack: 'web'
    };
    this.filterFunction = void 0;
    this.logger = void 0;
    this.filter = void 0;
    this.consentStatistics = void 0;
    this.consentAds = void 0;
    this.options = _extends({}, this.options, options);
    this.apiKey = apiKey;
    this.consentStatistics = false;
    this.consentAds = false;
    this.filterFunction = this.options.filterFunction || (() => true);
    this.filter = this.options.filter;
    this.logger = this.options.logger;
    if (typeof window !== 'undefined' && this.filter && !window.analytics) {
      initSegmentSnippet(this.apiKey);
    }
  }
  getPlatform() {
    return new Platform({
      name: this.options.name,
      version: this.options.minimumEventVersion.toString()
    });
  }
  getPlatformFamily() {
    const platform = this.getPlatform();
    if (this.options.stack === 'electron') return 'electron';
    return platform.app.family;
  }

  /**
   * @deprecated to optimise identify calls prefer adding consent settings
   * in your user's identify call.
   * @see identify
   */
  setConsent(newConsent) {
    this.consentAds = Boolean(newConsent.targetedAdvertising);
    this.consentStatistics = Boolean(newConsent.statistics);
    if (!this.isUsable()) {
      return;
    }
    return this.identify({
      targetedAds: this.consentAds,
      statistics: this.consentStatistics
    });
  }
  additionalSegmentData(protocols) {
    return _extends({
      integrations: this.getIntegrations(),
      device: {
        family: this.getPlatformFamily()
      },
      os: this.getPlatform().os
    }, protocols && {
      protocols
    });
  }
  getIntegrations() {
    return {
      All: false,
      'Braze Cloud Mode (Actions)': true,
      'Facebook Conversions API (Actions)': this.consentAds,
      'Rokt Integration': this.consentAds,
      Webhooks: this.consentAds,
      'Actions Google Enhanced Conversions': this.consentAds,
      'Google Analytics 4 Web': this.consentAds && this.consentStatistics,
      'Amplitude (Actions)': this.consentStatistics,
      'TikTok Conversions': this.consentAds,
      'Bing Ads': this.consentAds,
      'Snapchat Conversions API': this.consentAds,
      'Webhooks (Actions)': this.consentAds,
      'Google Analytics 4 Cloud': this.consentAds && this.consentStatistics,
      'Google Ads Conversions': this.consentAds
    };
  }
  /**
   * @description Check if cdp lib is usable
   * @returns
   */
  isUsable() {
    if (typeof window.analytics === 'undefined') {
      return false;
    }
    return this.filter;
  }

  /**
   * Inits CDP's SDKs and allows to set some later options to our CDPOptions
   * @param options
   * @returns
   */
  init() {
    if (this.isUsable()) {
      window.analytics.load == null || window.analytics.load(this.apiKey);
    }
  }

  /**
   * @description Generic method to log events (generic event and pageview event)
   * for correct event data, refer to your data plan
   **/
  logEvent(event) {
    var _this = this;
    return _asyncToGenerator(function* () {
      var _event$customAttribut;
      const dzr_unique_id = cookies.get('dzr_uniq_id');
      const defaultParams = getDefaultParams();
      const customParams = (_event$customAttribut = event.customAttributes) == null ? void 0 : _event$customAttribut.parameters;
      const parameters = customParams ? _extends({}, defaultParams, customParams) : defaultParams;
      const parametersNotEmpty = Object.keys(parameters != null ? parameters : {}).length > 0 ? parameters : undefined;
      if (!_this.isUsable()) {
        return;
      }
      if (!_this.filterFunction(event)) {
        return;
      }
      try {
        const protocols = {
          event_version: event.eventVersion || _this.options.minimumEventVersion
        };
        if (isPageViewEvent(event)) {
          var _event$customAttribut2;
          if (event != null && (_event$customAttribut2 = event.customAttributes) != null && _event$customAttribut2.screen_view_name) {
            return window.analytics.page(event.customAttributes.screen_view_name, _extends({}, event.customAttributes, dzr_unique_id && {
              dzr_unique_id
            }), _extends({}, _this.additionalSegmentData(protocols), {
              context: {
                page: _extends({
                  title: event.customAttributes.screen_view_name
                }, parametersNotEmpty && {
                  parameters: parametersNotEmpty
                })
              }
            }));
          }
        } else {
          return window.analytics.track(event.eventName, event.customAttributes, _this.additionalSegmentData(protocols));
        }
      } catch (e) {
        _this.logger == null || _this.logger('CDP lib - logEvent', e);
      }
    })();
  }

  /**
   * Passthrough to cdp.identify()
   * @see identify
   */
  login(identity) {
    return this.identify(identity);
  }

  /**
   * Method to sync Segment's user traits with our user data.
   * @param identity contain user information like email, customerid refer to type for more information
   */
  identify(identity) {
    var _this2 = this;
    return _asyncToGenerator(function* () {
      if (!_this2.isUsable()) {
        return;
      }
      if (identity.statistics) {
        _this2.consentStatistics = Boolean(identity.statistics);
      }
      if (identity.targetedAds) {
        _this2.consentAds = Boolean(identity.targetedAds);
      }

      // Extract URL parameters
      const urlParams = new URLSearchParams(window.location.search);

      // get domain
      const splittedHost = location.host.split('.');
      const domain = '.' + splittedHost[1] + '.com';
      try {
        if (_this2.consentStatistics) {
          for (const partner of partners) {
            if (urlParams.has(partner)) {
              const partnerValue = urlParams.get(partner);
              if (partnerValue) {
                let cookieName = partner.toLowerCase();
                // Explicit transformation for snapchat partner
                if (partner === 'ScCid') {
                  cookieName = 'sccid';
                }
                cookies.set(cookieName, partnerValue, domain, 365);
              }
            }
          }
        }
        const {
            userId
          } = identity,
          restOfIdentity = _objectWithoutPropertiesLoose(identity, _excluded);
        _this2.resetSegmentUser();
        if (userId && userId !== '0') {
          return window.analytics.identify(userId, restOfIdentity, _this2.additionalSegmentData());
        } else {
          return window.analytics.identify(restOfIdentity, _this2.additionalSegmentData());
        }
      } catch (e) {
        _this2.logger == null || _this2.logger('CDP lib - identify', e);
      }
    })();
  }

  /**
   * @description Useful in case of logout user
   */
  logout() {
    if (!this.isUsable()) {
      return;
    }
    try {
      window.analytics.reset();
    } catch (e) {
      var _this$logger;
      (_this$logger = this.logger) == null || _this$logger.call(this, 'CDP lib - logout', e);
    }
  }

  /**
   * @returns Current anonymousId user used in segment
   */
  getCurrentUserId() {
    if (!this.isUsable()) {
      return '';
    }
    return window.analytics.user == null ? void 0 : window.analytics.user().anonymousId();
  }

  /**
   * @returns Current user used in segment
   */
  getCurrentUser() {
    if (!this.isUsable()) {
      return undefined;
    }
    return window.analytics.user == null ? void 0 : window.analytics.user();
  }

  /**
   * @returns Current user traits used in segment
   */
  getCurrentUserTraits() {
    if (!this.isUsable()) {
      return undefined;
    }
    const user = window.analytics.user == null ? void 0 : window.analytics.user();
    const traits = user == null ? void 0 : user.traits();
    return traits;
  }

  /**
   * Reset segment user locally
   */
  resetSegmentUser() {
    if (this.isUsable()) {
      window.analytics.ready(() => {
        if ((window.analytics.user == null ? void 0 : window.analytics.user().id()) === '0') {
          this.logout();
        } else {
          var _window;
          (_window = window) == null || (_window = _window.analytics) == null || _window.user == null || (_window = _window.user()) == null || _window.traits({});
        }
      });
    }
  }
}
//# sourceMappingURL=cdpBuilder.js.map